// FILE: src/components/app/bases/TagsBaseComponent/TagsBaseComponent.jsx
import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import PropTypes from "prop-types";
import styles from "./TagsBaseComponent.module.css";

import TagsTreeComponent from "./TagsTreeComponent/TagsTreeComponent";
import TagsSingleComponent from "./TagsSingleComponent/TagsSingleComponent";
import AddTagsComponent from "./AddTagsComponent/AddTagsComponent";

import { buildTags, normalizeFullPath } from "../UploadDataComponent/utils/tagUtils";
import DataSourcePill from "../../../common/DataSourcePill/DataSourcePill";

import { TagsAPI, AuthAPI } from "../../../../utils/firebase";

const MOBILE_BP = 768;

const TagsBaseComponent = () => {
  const [userEmail, setUserEmail] = useState(null);
  const [rawTags, setRawTags] = useState([]);
  const [selected, setSelected] = useState(null);
  const [showAdd, setShowAdd] = useState(false);

  // Refs for smooth scroll on mobile
  const detailsRef = useRef(null);

  // Resolve current user email
  useEffect(() => {
    const { auth } = AuthAPI.getFirebase();
    if (auth?.currentUser?.email) {
      setUserEmail(String(auth.currentUser.email).toLowerCase());
    }
    const unsub = AuthAPI.onAuthChangedSafe((u) =>
      setUserEmail(u?.email ? String(u.email).toLowerCase() : null)
    );
    return () => unsub?.();
  }, []);

  // Live subscribe to user_tags (always Live DB)
  useEffect(() => {
    if (!userEmail) return;
    const unsub = TagsAPI.subscribeUserTags({
      userEmail,
      cap: 2000,
      onData: (docs) => setRawTags(Array.isArray(docs) ? docs : []),
      onError: (err) => console.error("[Tags] TagsAPI.subscribeUserTags error", err),
    });
    return () => unsub?.();
  }, [userEmail]);

  // Build hierarchy from NORMALIZED paths
  const { nodes, tree } = useMemo(() => {
    const paths = rawTags
      .map((t) => normalizeFullPath(t?.fullPath || t?.name || ""))
      .filter(Boolean);
    return buildTags(paths);
  }, [rawTags]);

  // Lookup map using SAME normalization as tree
  const nodeLookup = useMemo(() => {
    const m = new Map();
    for (const t of rawTags) {
      const key = normalizeFullPath(t?.fullPath || t?.name || "").toLowerCase();
      if (key) m.set(key, t);
    }
    return m;
  }, [rawTags]);

  const handleSelectNode = useCallback(
    (node) => {
      const key = normalizeFullPath(node?.fullPath || "").toLowerCase();
      const resolved = nodeLookup.get(key) || { ...node, fullPath: normalizeFullPath(node.fullPath) };
      setSelected(resolved);

      if (window.innerWidth <= MOBILE_BP && detailsRef.current) {
        setTimeout(() => {
          detailsRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 30);
      }
    },
    [nodeLookup]
  );

  return (
    <div className="container-fluid">
      <div className="pp-page">
        <div className="pp-page-header d-flex align-items-center justify-content-between">
          <div className="d-flex align-items-center gap-3">
            <h1 className="pp-page-title mb-0">Manage Tags</h1>
            <DataSourcePill fromCache={false} loading={false} showRefresh={false} />
          </div>
          <div className="pp-page-actions"></div>
        </div>

        <div className={styles.TagsBaseComponent}>
          <div className={styles.layout}>
            {/* LEFT / TOP: Tree */}
            <aside className={styles.leftPane}>
              <div className={styles.headerRow}>
                <div className="d-flex align-items-center gap-2">
                  {/* Keep the pill for parity with other pages */}
                </div>
                <div className="d-grid">
                  <button
                    type="button"
                    className="btn btn-primary btn-sm btn-compact"
                    onClick={() => setShowAdd(true)}
                  >
                    Add Tag
                  </button>
                </div>
              </div>

              <div className={styles.treePane}>
                <TagsTreeComponent
                  tree={tree}
                  onSelect={handleSelectNode}
                  selectedFullPath={selected?.fullPath || null}
                />
              </div>
            </aside>

            {/* RIGHT / BOTTOM: Details */}
            <section ref={detailsRef} className={styles.rightPane}>
              <TagsSingleComponent
                tag={selected}
                userEmail={userEmail}
                allTagPaths={Array.from(new Set(rawTags.map((t) => t.fullPath || t.name).filter(Boolean)))}
              />
            </section>
          </div>

          {showAdd && (
            <AddTagsComponent
              onClose={() => setShowAdd(false)}
              userEmail={userEmail}
              existingPaths={new Set(nodes.map((n) => n.fullPath))}
            />
          )}
        </div>
      </div>
    </div>
  );
};

TagsBaseComponent.propTypes = {};
TagsBaseComponent.defaultProps = {};
export default TagsBaseComponent;
